import React, { useContext } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { css } from '@emotion/react';
import uuid from 'react-uuid';

// redux
import { getFlatCourseAliases } from 'redux/selectors/course';
import {
  createAiCharacterObjection,
  deleteAiCharacterObjection,
} from 'redux/actions/ai-role-play';

import { cardIconSize, doubleSpacing, halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import {
  ReactComponent as AICharacter,
} from 'styles/icons/ai-characters.svg';
import { gray5, primary } from 'styles/global_defaults/colors';
import ClickableContainer from 'components/clickable-container';
import {
  ReactComponent as AIPersona,
} from 'styles/icons/ai-persona.svg';
import ObjectiveInputList from './objective-input-list';
import { AIPersonalities } from '..';
import EditAiInstructionContext from './context';
import AICharacterEditView from './ai-character-edit-modal';
import { getPersonalityLabel, getSelectedAvatarDisplayUrl } from './ai-character-utils';

const styles = css`
  .learner-role-input {
    width: calc(50% - ${halfSpacing}px);
  }

  .ai-character-container {
    border: 1px solid ${gray5};
    border-radius: ${quarterSpacing}px;
    display: flex;
  }

  .edit-btn-container {
    border: 1px solid ${primary} !important;
    border-radius: ${quarterSpacing}px;
  }

  .profile-image {
    width: ${cardIconSize}px;
    height: ${cardIconSize}px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

type CharacterSettingProps = {
  isEditView?: boolean;
  onEdit?: () => void;
  onBack?: () => void;
};

const CharacterSetting = (props: CharacterSettingProps) => {
  const { isEditView, onEdit, onBack } = props;
  const params = useLecturePageParams();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const personalities = [AIPersonalities.NEUTRAL, AIPersonalities.IRRITABLE, AIPersonalities.DISINTERESTED];

  const {
    setValue,
    trigger,
    getValues,
    control,
  } = useFormContext();
  const { aiRolePlayId } = useContext(EditAiInstructionContext) || {};
  const [isAddingNewAiObjective, setIsAddingNewAiObjective] = React.useState(false);
  const dispatch = useDispatch();

  const aiCharacterName = getValues('aiCharacterName');
  const aiCharacterRole = getValues('aiCharacterRole');
  const aiCharacterPersonality = getValues('aiCharacterPersonality');
  const aiCharacterFace = getValues('aiCharacterFace');
  const aiAvatarDisplayUrl = getSelectedAvatarDisplayUrl(aiCharacterFace);

  const isUnnamedCharacter = !aiCharacterName && !aiCharacterRole;
  const characterTitle = isUnnamedCharacter
    ? <i className='text-gray-2'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.UNNAMED_CHARACTER()}</i>
    : (
      <>
        <strong>{aiCharacterName}</strong>
        {` (${aiCharacterRole})`}
      </>
    );

  const displayPersonality = getPersonalityLabel(aiCharacterPersonality || AIPersonalities.NEUTRAL);

  const { fields, append } = useFieldArray({
    name: 'aiCharacterObjections',
    control,
    keyName: 'fieldId',
  });

  // We mutate control._defaultValues directly because RHF v7.22.1 doesn't support
  // shouldDirty: false on field array methods (append, remove) (added in v7.36.0). Public alternatives
  // like resetField reset both value and default (wiping pending text edits), and
  // reset(getValues(), { keepDirtyValues: true }) requires v7.31.0+. This is a
  // pragmatic tradeoff: it re-baselines defaults after already-saved structural
  // changes so isDirty reflects only unsaved text edits. Should be replaced with
  // native shouldDirty: false on append/remove if RHF is upgraded to v7.36.0+.
  const updateObjectionsDefaultValue = () => {
    const currentObjections = getValues('aiCharacterObjections') || [];
    // eslint-disable-next-line no-underscore-dangle
    control._defaultValues.aiCharacterObjections = currentObjections.map((obj) => ({ ...obj }));
  };

  const handleAddAiObjection = () => {
    if (aiRolePlayId) {
      setIsAddingNewAiObjective(true);
      dispatch(createAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        text: '',
      })).then((res) => {
        if (res) {
          // Use setValue instead of append to avoid marking the field as dirty,
          // since the objection is already saved to the backend
          const current = getValues('aiCharacterObjections') || [];
          setValue('aiCharacterObjections', [...current, { id: res.payload.id, text: '' }], { shouldDirty: false });
          updateObjectionsDefaultValue();
        }
      }).finally(() => {
        setIsAddingNewAiObjective(false);
      });
    } else {
      append({ id: uuid(), text: '' });
    }
  };

  const handleRemoveAiObjection = (aiCharacterObjectionId) => {
    if (fields.length <= 0) return;
    // Use setValue instead of remove to avoid marking the field as dirty,
    // since the deletion is already saved to the backend
    const current = getValues('aiCharacterObjections') || [];
    setValue(
      'aiCharacterObjections',
      current.filter((field) => field.id !== aiCharacterObjectionId),
      { shouldDirty: false },
    );
    updateObjectionsDefaultValue();
    if (aiRolePlayId && aiCharacterObjectionId) {
      dispatch(deleteAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        aiCharacterObjectionId,
      }));
    }
  };

  if (isEditView) {
    return (
      <AICharacterEditView
        onBackToRolePlay={onBack}
      />
    );
  }

  return (
    <div css={styles}>
      <div className='d-flex flex-column'>
        <div className='d-flex justify-content-center mt-8'>
          <AICharacter
            height={doubleSpacing}
            width={doubleSpacing}
          />
        </div>
        <p className='page-title text-large mb-5 mt-2 mx-auto text-black lh-35'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.THE_CHARACTERS()}
        </p>
      </div>
      <div>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER()}
        </p>
        <p className='text-large-body lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CUSTOMIZE_AI_CHARACTER_TEXT()}
        </p>
        <div className='ai-character-container align-items-center p-4 justify-content-between'>
          <div className='d-flex align-items-center'>
            {aiAvatarDisplayUrl ? (
              <img
                src={aiAvatarDisplayUrl}
                alt={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PROFILE.AVATAR()}
                className='profile-image'
              />
            ) : (
              <AIPersona
                width={cardIconSize}
                height={cardIconSize}
                role='img'
                aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_CHARACTER_AVATAR()}
              />
            )}
            <div className='ml-4'>
              <p className='mb-0 lh-25 text-large-body'>
                {characterTitle}
              </p>
              <p className='mb-0 lh-25 text-large-body'>
                <span className='font-weight-bolder'>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY_TEXT()}
                </span>
                {' '}
                {displayPersonality}
              </p>
            </div>
          </div>
          <ClickableContainer
            className='edit-btn-container px-3 py-1 align-items-center'
            onClick={onEdit}
            aria-label={t.FORM.EDIT()}
          >
            <div className='icon icon-edit icon-smallest text-small text-primary' />
            <span className='text-regular lh-25 pl-2 text-primary font-weight-bold'>{t.FORM.EDIT()}</span>
          </ClickableContainer>
        </div>
      </div>
      <div className='mt-5'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_OBJECTIONS()}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.TELL_AI_TO_SAY()}
        </p>
        <ObjectiveInputList
          name='aiCharacterObjections'
          fields={fields}
          handleAddField={handleAddAiObjection}
          handleRemoveField={handleRemoveAiObjection}
          isAddingField={isAddingNewAiObjective}
          maxFieldCount={5}
        />
      </div>
      <div className='mt-5'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARNER_ROLE(courseAliases)}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.WHAT_ROLE_PLAYING()}
        </p>
        <NvTextInput
          withForm
          name='learnerRole'
          className='border-gray-5 bg-white mt-2 learner-role-input required-input'
          inputClassName='text-large-body lh-25'
          required
        />
      </div>
    </div>
  );
};

export default CharacterSetting;
